import { supabase, getAccessToken } from "@/lib/supabase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** POST /api/ai/generate — works for both anonymous and authenticated callers. */
export async function generateAI({ toolType, payload, history = null, sessionId = null, projectContext = null, attachments = null }) {
  const token = await getAccessToken();
  const body = { tool_type: toolType, payload };
  if (Array.isArray(history) && history.length > 0) body.history = history;
  if (sessionId) body.session_id = sessionId;
  if (projectContext) body.project_context = projectContext;
  if (Array.isArray(attachments) && attachments.length > 0) body.attachments = attachments;

  const res = await fetch(`${API}/ai/generate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    let detail = "Generation failed";
    try {
      const data = await res.json();
      if (typeof data.detail === "string") detail = data.detail;
      else if (data.detail && typeof data.detail === "object") detail = data.detail.message || JSON.stringify(data.detail);
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
  return res.json();
}

/**
 * POST /api/ai/stream — token-by-token streaming over SSE-style frames.
 *
 * Uses fetch + ReadableStream (EventSource can't POST + send auth headers).
 * Each SSE frame is `data: {json}\n\n`. We buffer partial chunks and parse
 * one frame at a time.
 *
 * Callbacks:
 *   onStart()                    — first event arrived
 *   onModel(modelLabel)          — once
 *   onDelta(chunkText)           — many
 *   onDone({content, model, id, persisted, tool_type})  — exactly once on success
 *   onError(Error)               — once on failure (no further events after)
 *
 * Pass `signal` (AbortController.signal) to support Stop-generation.
 */
export async function streamAI({
  toolType, payload, history = null, sessionId = null, projectContext = null,
  attachments = null,
  signal, onStart, onModel, onDelta, onDone, onError,
}) {
  const token = await getAccessToken();
  const body = { tool_type: toolType, payload };
  if (Array.isArray(history) && history.length > 0) body.history = history;
  if (sessionId) body.session_id = sessionId;
  if (projectContext) body.project_context = projectContext;
  if (Array.isArray(attachments) && attachments.length > 0) body.attachments = attachments;

  let res;
  try {
    res = await fetch(`${API}/ai/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal,
    });
  } catch (e) {
    if (e?.name === "AbortError") return; // user pressed Stop before request started
    onError?.(new Error(e?.message || "Network error"));
    return;
  }

  if (!res.ok || !res.body) {
    let detail = "Stream failed";
    try {
      const text = await res.text();
      try {
        const data = JSON.parse(text);
        if (typeof data.detail === "string") detail = data.detail;
        else if (data.detail && typeof data.detail === "object") detail = data.detail.message || JSON.stringify(data.detail);
        else detail = text || detail;
      } catch { detail = text || detail; }
    } catch { /* ignore */ }
    onError?.(new Error(detail));
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const handleFrame = (raw) => {
    const line = raw.replace(/^data:\s?/, "").trim();
    if (!line) return;
    let ev;
    try { ev = JSON.parse(line); } catch { return; /* malformed — skip */ }
    switch (ev.type) {
      case "start": onStart?.(); break;
      case "model": onModel?.(ev.model); break;
      case "delta": onDelta?.(ev.content || ""); break;
      case "done":  onDone?.(ev); break;
      case "error": onError?.(new Error(ev.error || "Stream error")); break;
      default: break;
    }
  };

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx;
      // SSE frames are separated by a blank line (\n\n)
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        handleFrame(frame);
      }
    }
    if (buffer.trim()) handleFrame(buffer);
  } catch (e) {
    if (e?.name === "AbortError") return; // user stopped — silent
    onError?.(new Error(e?.message || "Stream read error"));
  } finally {
    try { reader.releaseLock(); } catch { /* ignore */ }
  }
}

export { supabase };
